import React, { FC } from 'react'
import {
  Button,
  ButtonType,
  Div,
  Flex,
  PricingTile,
  RadioOptionCardProps,
  Separator,
  Spacer,
  Transition,
  UpsaleSheet,
} from '@sendoutcards/quantum-design-ui'
import FeaturedCardsGrid from 'src/catalog/components/FeaturedCardsGrid/FeaturedCardsGrid'
import { useActions, useMemo, useQueryParams, useState } from 'src/hooks'
import InnerPaymentSheet, {
  UpsaleResponseData,
} from 'src/onboarding/components/InnerPaymentSheet'
import Disclaimer from 'src/marketing/plans/components/pricing_table/Disclaimer'

type ExpandedOptionChild = {
  expandedChild?: React.ReactNode
}

type OptionType = ExpandedOptionChild & RadioOptionCardProps

const discountDisclaimer = [
  '* Discounts apply to most gifts. Excludes: books, apparel, and business-building tools.',
]

const options: OptionType[] = [
  {
    title: '$97.00/Month',
    subtitle: 'For unlimited single card send',
    isFeatured: true,
    optionId: '3',
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            {
              feature: 'Send Unlimited Heartfelt Prompting Cards Each Month!',
              subFeatures: [
                'Unlimited Postcards (Postage included!)',
                'Unlimited Two-Panel Cards (Postage included!)',
                'A Heartfelt Prompting Card has an immediate send with a unique message to a single recipient.',
              ],
            },
            {
              feature:
                'Automated Group Sends (postage not included) - save when you send a group of cards all at once!',
              subFeatures: [
                'An Automated Send card has a feature send date or more than one recipient.',
                'Tiered pricing range: as low as $1.30 - $1.75 per card for 25+ cards in a group send.',
              ],
            },
            'Premier discounted shipping on top-selling gifts',
            'Save 30 percent discount on our bestselling gifts*',
            'Premium Digital Storage for pictures and addresses',
          ]}
          title="Unlimited"
          billingInterval="Monthly"
          price="$97"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
  {
    title: '$37.00/Month',
    subtitle: 'Send one free card per day',
    isFeatured: false,
    optionId: '6',
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            {
              feature:
                'Send 30 Heartfelt Prompting Postcards & Two-Panel Cards Each Month!',
              subFeatures: [
                'A Heartfelt Prompting Card has an immediate send with a unique message to a single recipient.',
                '$2.00 per card beyond the included 30 cards.',
              ],
            },
            {
              feature:
                'Automated Group Sends (postage not included) - save when you send a group of cards all at once!',
              subFeatures: [
                'An Automated Send card has a feature send date or more than one recipient.',
                'Tiered pricing range: as low as $1.55 - $1.85 per card for 25+ cards in a group send.',
              ],
            },
            'Save 20 percent on our bestselling gifts*',
            'Enhanced Digital Storage for pictures and addresses',
          ]}
          title="Card a Day"
          billingInterval="Monthly"
          price="$37"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
  {
    title: '$147.00/Month',
    subtitle: '',
    isFeatured: false,
    optionId: '4',
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            'Unlimited Heartfelt Prompting Cards** (postage included!)',
            '100 Automated Sends*** (postage not included)',
            '$1.25 per additional Automated Send*** (postage not included)',
            'Expense account required on this plan',
            'Premier discounted shipping on top-selling gifts',
            '30 percent discount on gifts*',
            'Premium Storage',
            'Unlimited Campaign Store Downloads',
          ]}
          title="Unlimited +100 Automated Sends"
          billingInterval="Monthly"
          price="$147"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
  {
    title: '$247.00/Month',
    subtitle: '',
    isFeatured: false,
    optionId: '39',
    isSelected: false,
    expandedChild: (
      <>
        <PricingTile
          features={[
            'Unlimited Heartfelt Prompting Cards** (postage included!)',
            '250 Automated Sends*** (postage not included)',
            '$1.00 per additional Automated Send*** (postage not included)',
            'Expense account required on this plan',
            'Premier discounted shipping on top-selling gifts',
            '30 percent discount on gifts*',
            'Premium Storage',
            'Unlimited Campaign Store Downloads',
          ]}
          title="Unlimited +250 Automated Sends"
          billingInterval="Monthly"
          price="$247"
          priceSubtext="/month"
          hasShadow={false}
        />
        <Div outset={{ horizontal: 'x4' }}>
          <Separator orientation="horizontal" />
          <Spacer space="x2" />
          <Disclaimer messages={discountDisclaimer} />
        </Div>
      </>
    ),
  },
]

type CardUpsaleProps = {
  selectedPlanId?: string
  onClose?: () => void
}

const getPlan = (planId: string) => {
  if (planId === '7') {
    return '4'
  } else if (planId === '8') {
    return '39'
  }
  return planId
}

export const CardUpsale: FC<CardUpsaleProps> = props => {
  const { selectedPlanId = '', onClose } = props

  const [isOpen, setIsOpen] = useState(true)
  const [planId, setPlanId] = useState(
    selectedPlanId ? getPlan(selectedPlanId) : '',
  )
  const [shouldGoToPricing, setShouldGoToPricing] = useState(false)
  const { promoPlanId } = useQueryParams()
  const [numberOfOptions, setNumberOfOptions] = useState(1)
  const actions = useActions()
  const upsaleSheetTitle = useMemo(() => {
    return promoPlanId === '3'
      ? 'Send Unlimited Postcards!'
      : 'Want to send more?'
  }, [promoPlanId])
  const upsaleSheetDescription = useMemo(() => {
    return promoPlanId === '3'
      ? 'To send as many postcards as you want and to access some great benefits, choose our $97 Unlimited subscription! Click Subscribe Now to sign up or view other options!'
      : 'To send more cards and to access some great benefits, upgrade to one of our paid accounts. You can send as many cards as you want (yes, thats right) on our $97 Unlimited subscription! Or a card a day on our $37 subscription! Click to sign up or view our other packages!'
  }, [promoPlanId])

  const [upsaleResponseData, setUpsaleResponseData] = useState<
    UpsaleResponseData | undefined
  >(undefined)

  const onViewMoreLink = () => {
    if (shouldGoToPricing) {
      window.location.href = 'https://app.sendoutcards.com/pricing'
    } else {
      setShouldGoToPricing(true)
      setNumberOfOptions(2)
    }
  }

  const submitAction = {
    title: 'Subscribe Now',
    type: 'secondary' as ButtonType,
    onClick: (optionId: string) => setPlanId(optionId),
  }

  const viewMoreLink = {
    onClick: onViewMoreLink,
    title: 'View More Options >',
  }

  const handleSetIsOpen = () => {
    setIsOpen(!isOpen)
    if (onClose) {
      onClose()
    } else {
      actions.clearRouteArgs()
      actions.openAccount()
    }
  }

  const updateUpsaleResponseData = (
    uResponseData: UpsaleResponseData | undefined,
  ) => {
    setUpsaleResponseData(uResponseData)
  }

  return (
    <UpsaleSheet
      setIsOpen={handleSetIsOpen}
      isOpen={isOpen}
      title={upsaleSheetTitle}
      description={upsaleSheetDescription}
      options={options}
      submitAction={submitAction}
      extraLink={viewMoreLink}
      selectedOptionTitle="Great choice"
      selectedOptionDescription="We are so happy with your choice"
      backgroundElement={<FeaturedCardsGrid />}
      selectedOption={planId}
      defaultNumOptionsShown={numberOfOptions}
      successComponent={
        typeof upsaleResponseData !== undefined && upsaleResponseData ? (
          <Flex
            alignItems={'center'}
            justifyContent={'center'}
            width={'100%'}
            flexDirection={'column'}
            height={'100%'}
          >
            <Div outset={{ top: 'auto' }} width={'100%'}>
              <Transition
                title={upsaleResponseData.title}
                isLoading={upsaleResponseData.isLoading}
                subtitle={upsaleResponseData.subTitle}
                loadedStatus={upsaleResponseData.loadedStatus}
              />
            </Div>
            {upsaleResponseData.loadedStatus !== undefined && (
              <Div outset={{ top: 'auto' }} width={'100%'}>
                {upsaleResponseData.loadedStatus === 'success' && (
                  <Button
                    fullWidth={true}
                    title={'Continue to account'}
                    onClick={() => {
                      if (onClose) {
                        onClose()
                      } else {
                        actions.openAccount()
                      }
                    }}
                  />
                )}
                {upsaleResponseData.loadedStatus === 'error' && (
                  <Button
                    fullWidth={true}
                    title={'Go back'}
                    onClick={() => setUpsaleResponseData(undefined)}
                  />
                )}
              </Div>
            )}
          </Flex>
        ) : undefined
      }
    >
      <InnerPaymentSheet
        planId={planId}
        updateUpsaleResponseData={updateUpsaleResponseData}
      />
    </UpsaleSheet>
  )
}

export default CardUpsale
